import _ from 'lodash';
import { types as repr, graphql as coreGql } from '@sitebuilder/core';
import {
    graphql as commonGql,
    localization as commonLocalization,
    utils as commonUtils,
} from './index';

export function hashString(message: string) {
    let hash = 0;
    for (let i = 0; i < message.length; i++) {
        hash = (hash * 31 + message.charCodeAt(i)) >>> 0; // Keep it in 32-bit range
    }
    return hash.toString(16); // Return as hex string
}

export function hashObject(obj: any) {
    const message = JSON.stringify(obj);
    return hashString(message);
}

export const convertUTCToLocalDate = (date: Date) => {
    if (!date) {
        return date;
    }
    date = new Date(date);
    date = new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
    return date;
};

export const convertLocalToUTCDate = (date: Date) => {
    if (!date) {
        return date;
    }
    date = new Date(date);
    date = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    return date;
};

declare global {
    interface Window {
        // datafront attributes
        [key: string]: any;
    }
}

export const transformDataWithGlobals = (
    transformFunctionName: string,
    functionToTransform: string | undefined,
    args: any
) => {
    let transformedData = null;

    try {
        if (typeof window !== 'undefined' && typeof window[transformFunctionName] === 'function') {
            transformedData = (window[transformFunctionName] as Function)(args);
        } else {
            transformedData = transformWithGlobals(functionToTransform, args);
        }
    } catch (e) {
        if (typeof window !== 'undefined') {
            console.error(`Error transforming data with ${transformFunctionName}`, e);
        }
    }

    return transformedData;
};

export const transformWithGlobals = (src: string | undefined, ...args: any[]) => {
    if (typeof src === 'string' && src.length > 0) {
        try {
            // eslint-disable-next-line no-new-func
            const transformFunction = Function(
                `return (({ _ }, ...rest) => {
                    // Preserve the original console
                    const originalConsole = { ...console };

                    // On server, override the console
                    if (typeof window === 'undefined') {
                        // Override console methods with empty functions
                        const noopConsole = {
                            log: () => {},
                            error: () => {},
                            warn: () => {},
                            info: () => {},
                            debug: () => {},
                        };

                        // Replace the function's console with the appropriate console object
                        Object.assign(console, noopConsole);
                    }
                    
                    const result = (() => { ${src} })().apply(null, rest);

                    // Restore the original console
                    Object.assign(console, originalConsole);
                    
                    return result;
                })`
            )();

            if (transformFunction) {
                return transformFunction.apply(null, [{ _ }, ...args]);
            }
        } catch (err) {
            console.error('error evaluating script', err);
        }
    }
};

export const RGBToHex = (rgb: string) => {
    // Choose correct separator
    const sep = rgb.indexOf(',') > -1 ? ',' : ' ';
    // Turn "rgb(r,g,b)" into [r,g,b]
    const rgbValue: string[] = rgb.substr(4).split(')')[0].split(sep);

    let r = (+rgbValue[0]).toString(16);
    let g = (+rgbValue[1]).toString(16);
    let b = (+rgbValue[2]).toString(16);

    if (r.length === 1) r = '0' + r;
    if (g.length === 1) g = '0' + g;
    if (b.length === 1) b = '0' + b;

    return '#' + r + g + b;
};

export const isJSON = (str: string) => {
    try {
        return JSON.parse(str) && !!str;
    } catch (e) {
        return false;
    }
};

type PageData = repr.PageData & { resourceStrings?: { [key: string]: string } };

const transformRootDocumentData = (data: any, functionToTransform: string) => {
    let transformedData: any = { data };

    try {
        // eslint-disable-next-line no-new-func
        const transformedProps = commonUtils.transformWithGlobals(functionToTransform, {
            ...transformedData,
        });

        if (transformedProps) {
            transformedData = { ...transformedData, ...transformedProps };
        }
    } catch (e) {
        console.log(e);
    }

    return transformedData.data;
};

function fixDocumentResourceStrings(resourceStrings: any) {
    const resources: any = {};

    if (resourceStrings && Array.isArray(resourceStrings)) {
        resourceStrings?.forEach((rString: any) => {
            Object.keys(rString)
                ?.filter((key: string) => key !== 'id')
                ?.forEach((k: string) => {
                    const locale = k === 'en' ? 'en-US' : k === 'fr' ? 'fr-CA' : k;
                    resources[locale] = {
                        ...resources[locale],
                        [rString.id]: rString[k],
                    };
                });
        });
    }

    return resources;
}

const transformProfileId = (profileId: any, functionToTransform: string) => {
    let transformedId: any = profileId;

    try {
        // eslint-disable-next-line no-new-func
        const _transformedId = commonUtils.transformWithGlobals(functionToTransform, profileId);

        if (_transformedId) {
            transformedId = _transformedId;
        }
    } catch (e) {
        console.log(e);
    }

    return transformedId;
};

export async function fetchPageData(
    document: repr.Document,
    graphqlClient: coreGql.IGraphqlClient,
    siteResources: repr.Resources | null,
    params: { profileId: string; locale: string },
    requestBody: any = undefined
): Promise<PageData> {
    const rootDocProperties: any = document?.widgetInstances?.root?.properties;
    const { pageTitleEn, pageTitleFr, dataSource, dataType, ...rest } = rootDocProperties || {};
    let { profileId, locale } = params;

    let fileName = `${profileId}-${locale}`;

    if (rest.profileIdTransform) {
        profileId = transformProfileId(profileId, rest.functionToProfileIdTransform);
    }

    const x: PageData & {
        requestBody?: any;
    } = { metadata: { locale }, data: {} };

    if (dataSource && dataType !== 'none') {
        if (dataType === 'fundList') {
            const fundListData = await commonGql.fundList.fetchFundListData(
                dataSource,
                {
                    locale,
                    includeProfiles: rest.includeAllProfiles,
                    includeAnnotations: rest.includeAnnotations,
                    annotationFilterProperty: rest.annotationFilterProperty,
                },
                graphqlClient
            );
            x.data = fundListData;
        } else {
            const profileData = await commonGql.webProfiles.fetchWebProfileData(
                dataSource,
                {
                    profileId,
                    locale,
                    includeAllProfiles: rest.includeAllProfiles,
                    includeHistoricalHoldings: rest.includeHistoricalHoldings,
                    includeUnderlyingFundPerformance: rest.includeUnderlyingFundPerformance,
                    includeFundSeriesPerformance: rest.includeFundSeriesPerformance,
                    annotationFilterProperty: rest.annotationFilterProperty,
                },
                graphqlClient
            );
            x.data = profileData;

            if (profileData.pdfFilename) {
                fileName = profileData.pdfFilename;
            }
        }
    }

    x.rawData = x.data;
    if (rest.isTransformData && rest.functionToTransform) {
        x.data = transformRootDocumentData({ ...x.data, profileId }, rest.functionToTransform);
    }

    x.data.requestBody = requestBody;

    if (x.data) {
        const pageResourceStrings = fixDocumentResourceStrings(rest.resourceStrings);

        const finalData = {
            ...x.data,
            profileId,
            resourceStrings: {
                ...rest.resourceStrings?.[locale],
                ...siteResources?.[locale],
                ...pageResourceStrings[locale],
            },
        };

        x.data.resourceStrings = finalData.resourceStrings;

        const i18n = commonLocalization.getLocalization(locale, 'CAD', finalData.resourceStrings);

        let pageTitle: string = locale === 'en-US' ? pageTitleEn : pageTitleFr;

        // if the meta title was provided in page metadata, databind and use the value
        if (rest.pageTitle) {
            pageTitle = i18n.format(rest.pageTitle, finalData) ?? pageTitle;
        }

        // if the filename was provided in page metadata, databind and use the value
        if (rest.filename) {
            fileName = i18n.format(rest.fileName, finalData) ?? fileName;
        }

        if (rest.redirect301) {
            const redirect301 = i18n.format(rest.redirect301, finalData) ?? null;
            rest.redirect301 = redirect301;
        }

        if (rest.redirect302) {
            const redirect302 = i18n.format(rest.redirect302, finalData) ?? null;
            rest.redirect301 = redirect302;
        }

        if (rest.redirect301) {
            x.status = 301;
            x.headers = {
                Location: rest.redirect301,
            };
        } else if (rest.redirect302) {
            x.status = 302;
            x.headers = {
                Location: rest.redirect302,
            };
        }

        x.metadata = {
            locale,
            fileName,
            pageTitle,
            pageDescription: rest.pageDescription
                ? i18n.format(rest.pageDescription, finalData)
                : '',
            pageAuthor: rest.pageAuthor ? i18n.format(rest.pageAuthor, finalData) : '',
            pageSubject: rest.pageSubject ? i18n.format(rest.pageSubject, finalData) : '',
            pageCreator: rest.pageCreator ? i18n.format(rest.pageCreator, finalData) : '',
            keywords: rest.keywords ? i18n.format(rest.keywords, finalData) : '',
            canonicalURL: rest.canonicalURL ? i18n.format(rest.canonicalURL, finalData) : '',
            externalCSSLink: rest.externalCSSLink,
            robots: rest.robots || '',
        };
    } else {
        // error page or not found?
    }

    return x;
}
